/*
 * Copyright (C) 2020 Graylog, Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the Server Side Public License, version 1,
 * as published by MongoDB, Inc.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * Server Side Public License for more details.
 *
 * You should have received a copy of the Server Side Public License
 * along with this program. If not, see
 * <http://www.mongodb.com/licensing/server-side-public-license>.
 */
import React from 'react';

import CommonNotificationSummary
  from 'components/event-notifications/event-notification-types/CommonNotificationSummary';
import type { CreateInvestigationNotificationSummaryType } from 'security-app/types';
import {
  useGetPriorities,
  useGetUsers,
  useGetTeams,
  useGetInvestigations,
} from 'security-app/hooks/useInvestigationsAPI';
import { usePagination } from 'security-app/components/common/contexts';

function CreateInvestigationNotificationSummary({
  notification,
  ...restProps
}: CreateInvestigationNotificationSummaryType) {
  const localPagination = usePagination();

  const { investigations } = useGetInvestigations(localPagination);
  const investigationsMap = React.useMemo(() => ({
    ...investigations.reduce((acc, investigation) => ({ ...acc, [investigation.id]: { label: investigation.name } }), {}),
  }), [investigations]);

  const { users } = useGetUsers(true);
  const { teams } = useGetTeams(true);
  const usersTeamsMap = React.useMemo(() => ({
    ...users.reduce((acc, user) => ({ ...acc, [user.id]: { label: user.full_name, icon: 'user' } }), {}),
    ...teams.reduce((acc, team) => ({ ...acc, [team.id]: { label: team.name, icon: 'users' } }), {}),
  }), [users, teams]);

  const { priorities } = useGetPriorities(true);
  const getPriorityText = React.useCallback((value: number) => (value ? priorities.find((priority) => priority.priority === value)?.text : ''), [priorities]);

  return (
    <CommonNotificationSummary {...restProps} notification={notification}>
      <tr>
        <td>Create a New Investigation for Every Alert</td>
        <td>{notification.config.create_new_investigation ? <em>True</em> : <em>False</em>}</td>
      </tr>
      {!notification.config.create_new_investigation && (
        <tr>
          <td>Investigation</td>
          <td>{investigationsMap[notification.config.investigation_id]?.label}</td>
        </tr>
      )}
      <tr>
        <td>Assign Investigation To</td>
        <td>{usersTeamsMap[notification.config.investigation_assigned_to]?.label}</td>
      </tr>
      <tr>
        <td>Investigation Priority</td>
        <td>{getPriorityText(notification.config.investigation_priority)}</td>
      </tr>
      <tr>
        <td>Investigation Status</td>
        <td>{notification.config.investigation_status}</td>
      </tr>
    </CommonNotificationSummary>
  );
}

CreateInvestigationNotificationSummary.defaultProps = {
  notification: {},
};

export default CreateInvestigationNotificationSummary;
